import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ENVIRONMENT, LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { BaseUser, BusinessProfile, Environment, Timezone, UserAccount } from '@iot-platform/models/common';
import { NotificationService } from '@iot-platform/notification';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import moment from 'moment-timezone';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { AuthSession, SignInResult } from '../models/auth-session.model';
import { SsoCodeToTokensResponse, SsoRefreshTokensResponse } from '../models/sso-code-to-tokens-response';
import { AuthProvider } from '../providers/auth-provider';

/** A session is refreshed when its id token expires within this delay (30 minutes). */
const SESSION_MIN_VALIDITY_MS = 30 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly router: Router = inject(Router);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly authProvider: AuthProvider = inject(AuthProvider);
  private readonly notificationService: NotificationService = inject(NotificationService);

  getSsoTokensFromCode(code: string): Observable<SsoCodeToTokensResponse> {
    return this.authProvider.exchangeSsoCode(code);
  }

  public refreshSsoTokens(refreshToken: string): Observable<SsoRefreshTokensResponse> {
    return this.authProvider.refreshSsoTokens(refreshToken);
  }

  storeSsoTokens({ idToken, accessToken, tokenType, refreshToken, expiresIn }: Record<string, string>) {
    this.storage.set(LocalStorageKeys.STORAGE_SSO_TOKEN_ID, idToken);
    this.storage.set(LocalStorageKeys.STORAGE_SSO_ACCESS_TOKEN, accessToken);
    this.storage.set(LocalStorageKeys.STORAGE_SSO_TOKEN_TYPE, tokenType);
    this.storage.set(LocalStorageKeys.STORAGE_SSO_EXPIRES_IN, expiresIn);
    this.storage.set(LocalStorageKeys.STORAGE_SSO_EXPIRES_AT, new Date(Date.now() + Number.parseInt(expiresIn, 10) * 1000).toISOString());
    this.storage.set(LocalStorageKeys.STORAGE_SSO_LOGGED_IN_AT, String(Date.now()));
    this.storage.set(LocalStorageKeys.STORAGE_SSO_LOGGED_IN_WITH_SSO, 'true');
    if (refreshToken) {
      this.storage.set(LocalStorageKeys.STORAGE_SSO_REFRESH_TOKEN, refreshToken);
    }
  }

  signWithRefreshToken(token: string, userId: string): Observable<boolean> {
    return this.authProvider.signInWithRefreshToken(token, userId);
  }

  isValidSession(): Observable<boolean> {
    return this.authProvider.isValidSession().pipe(
      switchMap((valid) =>
        valid
          ? this.authProvider.getSession().pipe(
              tap((session) => this.storeIdToken(session)),
              map(() => true),
              catchError(() => of(false))
            )
          : of(false)
      )
    );
  }

  getAccount(headers: HttpHeaders): Observable<UserAccount> {
    return this.httpClient.get<UserAccount>(`${this.environment.api.url}/account`, { headers }).pipe(retry(1));
  }

  public signIn(username: string, password: string): Observable<SignInResult> {
    return this.authProvider.signIn(username, password).pipe(
      tap((result) => {
        if (result.status === 'SIGNED_IN') {
          this.storeIdToken(result.session);
        }
      })
    );
  }

  public signInWithSso(): void {
    this.authProvider.signInWithSso();
  }

  public validateSsoTokens(idToken: string) {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${idToken}`);
    return this.getAccount(headers);
  }

  public retrieveSsoTokens() {
    return {
      idToken: this.storage.get(LocalStorageKeys.STORAGE_SSO_TOKEN_ID),
      accessToken: this.storage.get(LocalStorageKeys.STORAGE_SSO_ACCESS_TOKEN),
      refreshToken: this.storage.get(LocalStorageKeys.STORAGE_SSO_REFRESH_TOKEN)
    };
  }

  public isLoggedInWithSSO() {
    return this.storage.get(LocalStorageKeys.STORAGE_SSO_LOGGED_IN_WITH_SSO) === 'true';
  }

  public forgotPassword(username: string): Observable<void> {
    return this.authProvider.forgotPassword(username);
  }

  public forgotPasswordSubmit(credentials: { username: string; code: string; password: string }): Observable<void> {
    return this.authProvider.confirmForgotPassword(credentials);
  }

  public completeNewPassword(newPassword: string): Observable<AuthSession> {
    return this.authProvider.completeNewPassword(newPassword).pipe(tap((session) => this.storeIdToken(session)));
  }

  public logout(): Observable<void> {
    this.notificationService.error.set(null);
    return this.authProvider.signOut().pipe(catchError(() => of(undefined)));
  }

  public loadAccount(): Observable<UserAccount> {
    // In account we have : User infos, preferences, business profiles, privileges
    return this.httpClient.get<UserAccount>(`${this.environment.api.url}/account`).pipe(
      map((account) => {
        const businessProfiles = account.businessProfiles.map((profile: BusinessProfile): BusinessProfile => {
          // Kept as before: `name`/`offset` are null when the profile has no timezone (the shared `Timezone` model is non-nullable).
          const timezoneDetails = {
            name: profile.timezoneDetails ? profile.timezoneDetails.name : null,
            offset: profile.timezoneDetails && profile.timezoneDetails.name ? moment().tz(profile.timezoneDetails.name).format('Z') : null
          } as Timezone;

          return { ...profile, timezoneDetails };
        });
        return { ...account, businessProfiles };
      })
    );
  }

  public retrieveSession(): Observable<AuthSession> {
    return this.authProvider.getSession({ minValidityMs: SESSION_MIN_VALIDITY_MS }).pipe(
      tap((session) => this.storeIdToken(session)),
      catchError((error: unknown) => {
        this.clearStorageAndGoToLoginPage();
        return throwError(() => error);
      })
    );
  }

  public refreshToken(): Observable<AuthSession> {
    return this.authProvider.getSession({ minValidityMs: SESSION_MIN_VALIDITY_MS }).pipe(tap((session) => this.storeIdToken(session)));
  }

  /** Expiration date of a JWT, `null` when it has no `exp` claim. */
  getTokenExpirationDate(token: string): Date | null {
    const decoded: JwtPayload = jwtDecode<JwtPayload>(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  clearStorageAndGoToLoginPage() {
    this.storage.clear();
    this.router.navigate(['/login']);
  }

  /** A token without `exp` claim never expires (RFC 7519 §4.1.4). */
  public tokenExpired(token: string): boolean {
    const expiration = this.getTokenExpirationDate(token);
    if (!expiration) {
      return false;
    }
    return !(expiration.valueOf() > Date.now());
  }

  public ssoTokenExpired(ssoTokenExpiresAt: string): boolean {
    if (!ssoTokenExpiresAt) {
      return true;
    }
    return new Date(ssoTokenExpiresAt).getTime() - Date.now() <= 0;
  }

  isUserAdmin(sessionEntityId: string, currentUserId: string): Observable<boolean> {
    if (!sessionEntityId) {
      return of(false);
    }
    return this.httpClient
      .get<{ content: BaseUser[] }>(`${this.environment.api.url}/entities/${sessionEntityId}/admin-profiles`)
      .pipe(map((response) => response.content.some((user) => user.id === currentUserId)));
  }

  private storeIdToken(session: AuthSession): void {
    if (session.tokens.idToken) {
      this.storage.set(LocalStorageKeys.STORAGE_ID_TOKEN_KEY, session.tokens.idToken);
    }
  }
}
